import type { AxiosError } from 'axios';

interface RetryConfig {
  maxRetries: number;
  shouldRetry?: (error: unknown) => boolean;
  delayMs?: number;
}

interface RetryResult<T> {
  data?: T;
  error?: unknown;
  retryCount: number;
  statusCode?: number;
}

export async function retryWithDelay<T>(
  fn: () => Promise<T>,
  config: RetryConfig
): Promise<RetryResult<T>> {
  const { maxRetries, shouldRetry, delayMs = 3000 } = config;
  let lastError: unknown;
  let retryCount = 0;
  let statusCode: number | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const data = await fn();
      return { data, retryCount };
    } catch (error) {
      lastError = error;
      retryCount = attempt;

      if ((error as AxiosError)?.response?.status) {
        statusCode = (error as AxiosError).response?.status;
      }

      const canRetry = attempt < maxRetries;
      const shouldRetryError = shouldRetry ? shouldRetry(error) : true;

      if (!canRetry || !shouldRetryError) {
        break;
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return { error: lastError, retryCount, statusCode };
}

export function shouldRetryError(error: unknown): boolean {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;

  if (status === 404) {
    return false;
  }

  return true;
}
